import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, RadioGroup } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface Stakeholder {
  id: string;
  name: string;
  caresAbout: string;
  influence: string;
  role: string;
  cadence: string;
}

export default function StakeholderMapPage() {
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([
    { id: '1', name: '', caresAbout: '', influence: '', role: '', cadence: '' },
    { id: '2', name: '', caresAbout: '', influence: '', role: '', cadence: '' },
    { id: '3', name: '', caresAbout: '', influence: '', role: '', cadence: '' },
  ]);
  const [hiddenVoices, setHiddenVoices] = useState('');

  const updateStakeholder = (id: string, field: keyof Stakeholder, value: string) => {
    setStakeholders(stakeholders.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const addStakeholder = () => {
    setStakeholders([...stakeholders, { 
      id: Date.now().toString(), 
      name: '', 
      caresAbout: '', 
      influence: '', 
      role: '', 
      cadence: '' 
    }]);
  };

  return (
    <TaskPage
      phase="engage"
      title="Stakeholder Map"
      pageNumber={2}
      instruction="List people/groups who can decide, deliver, block, or are meaningfully affected."
      whyItMatters="Understanding who matters and why helps prevent surprises and ensures alignment."
      coachRail={{
        timing: "10 minutes",
        tips: "Build the first pass silently, then compare. Differences spotlight assumptions.",
        debrief: "Who did we miss? What patterns do we see?"
      }}
    >
      <div className="space-y-6">
        {/* Stakeholder Table */}
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Stakeholder Map
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[var(--hover-tint)] border-b-2 border-[var(--rule)]">
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">Stakeholder</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">What they care about</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">Influence (L/M/H)</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">Role (R/A/C/I)</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">Involvement cadence</th>
                </tr>
              </thead>
              <tbody>
                {stakeholders.map((stakeholder) => (
                  <tr key={stakeholder.id} className="border-b border-[var(--rule)]">
                    <td className="p-2">
                      <TextInput
                        value={stakeholder.name}
                        onChange={(e) => updateStakeholder(stakeholder.id, 'name', e.target.value)}
                        placeholder="Name or group"
                        className="text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <TextInput
                        value={stakeholder.caresAbout}
                        onChange={(e) => updateStakeholder(stakeholder.id, 'caresAbout', e.target.value)}
                        placeholder="What matters to them"
                        className="text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <RadioGroup
                        options={[
                          { value: 'L', label: 'L' },
                          { value: 'M', label: 'M' },
                          { value: 'H', label: 'H' }
                        ]}
                        value={stakeholder.influence}
                        onChange={(value) => updateStakeholder(stakeholder.id, 'influence', value)}
                      />
                    </td>
                    <td className="p-2">
                      <RadioGroup
                        options={[
                          { value: 'R', label: 'R' },
                          { value: 'A', label: 'A' },
                          { value: 'C', label: 'C' },
                          { value: 'I', label: 'I' }
                        ]}
                        value={stakeholder.role}
                        onChange={(value) => updateStakeholder(stakeholder.id, 'role', value)}
                      />
                    </td>
                    <td className="p-2">
                      <TextInput
                        value={stakeholder.cadence}
                        onChange={(e) => updateStakeholder(stakeholder.id, 'cadence', e.target.value)}
                        placeholder="How often"
                        className="text-sm"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={addStakeholder}
              className="mt-4 w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
            >
              + Add another stakeholder
            </button>
          </div>
        </div>

        {/* Hidden Voices Check */}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">
            Hidden Voices Check
          </h3>
          <p className="text-sm text-[var(--text-sec)] mb-3">Who isn't here but will feel the impact? Add them.</p>
          <textarea
            value={hiddenVoices}
            onChange={(e) => setHiddenVoices(e.target.value)}
            placeholder="List people or groups who might be affected but aren't represented..."
            className="w-full px-3 py-2 border border-[var(--slate)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
          />
        </div>

        <Callout type="tip" phase="engage">
          <div className="space-y-2 text-sm">
            <p><strong>Role codes:</strong> R = Responsible, A = Accountable, C = Consulted, I = Informed</p>
            <p><strong>Influence:</strong> L = Low, M = Medium, H = High</p>
          </div>
        </Callout>
      </div>
    </TaskPage>
  );
}






